/**
 * CMP source type - indicates which consent management platform is active
 */
export type CMPSource = 'skyscanner' | 'ketch';

/**
 * Configuration for initTrackingPreferences
 */
export interface TrackingPreferencesConfig {
  /** Enable Ketch CMP instead of Skyscanner's default CMP */
  enableKetchCMP: boolean;
  /** Consumer identifier for logging (e.g., 'banana', 'falcon') */
  consumer: string;
  /** Ketch SDK script URL to use when loading the SDK */
  ketchScriptUrl: string;
  /** Custom cookie name for Ketch consent (default: '_swb_consent_') */
  ketchConsentCookieName?: string;
  /** Custom localStorage key for TCF data (default: '_ketch_tcf_tcdata_v1_') */
  ketchTCFStorageKey?: string;
  /** Enable purpose-scoped ID service integration (calls backend on consent changes) */
  enablePurposeScopedIdService: boolean;
  /** Custom URL for purpose-scoped ID service legacy CMP endpoint */
  purposeScopedIdServiceLegacyCmpUrl?: string;
  /** Custom URL for purpose-scoped ID service Ketch CMP endpoint */
  purposeScopedIdServiceKetchCmpUrl?: string;
  /**
   * The user's market code (e.g., 'UK', 'US'). Required to trigger automatic
   * migration of existing users (migrateExistingUserTracking) and provisioning
   * of non-regulated market users (provisionNonRegulatedMarketTracking).
   */
  market?: string;
  /**
   * Enable cookie version rollback functionality. When true, users with
   * a higher cookie version than the current target version will have
   * their cookie downgraded. This is intended for rollback scenarios when
   * a feature flag needs to be turned off after users received a higher version.
   * Default: false (cookie versions are never downgraded)
   */
  enableCookieVersionRollback?: boolean;
  /**
   * Enable provisioning of purpose-scoped IDs for existing GDPR users.
   * When true, migrateExistingUserTracking() will provision tracking IDs for
   * existing v3 cookie users with consent in regulated markets and bump their cookie to v4.
   * Requires enablePurposeScopedIdService to also be true.
   * Only applies to legacy (skyscanner) CMP mode, not Ketch mode.
   * Default: false
   */
  enablePurposeScopedIdExistingGDPRUsers?: boolean;
  /**
   * Enable provisioning of purpose-scoped IDs for non-regulated market users.
   * When true, provisionNonRegulatedMarketTracking() will provision tracking IDs
   * for users in non-regulated markets (e.g., US, JP) who have implicit consent.
   * Requires enablePurposeScopedIdService to also be true.
   * Only applies to legacy (skyscanner) CMP mode, not Ketch mode.
   * Default: false
   */
  enablePurposeScopedIdNonRegulatedMarkets?: boolean;
  /**
   * Enable PSID (purpose-scoped ID) sync when Ketch's cachedConsent event fires.
   * When true, reads the _swb_consent_ cookie and sends it to the purpose-scoped-id-service.
   * NOTE: Requires `enablePurposeScopedIdService` to also be true for sync to occur.
   * Default: false
   */
  enableKetchPsidSync?: boolean;
  /**
   * Enable bidirectional consent sync between legacy gdpr cookie and Ketch.
   *
   * Forward: at boot, queues `setConsent` (purposes + vendorConsents decoded
   * from the legacy tcString) on the Ketch semaphore via `once('identities')`,
   * and suppresses the consent banner for the migration window. Vendors
   * present only on the Ketch property (not in the legacy tcString) inherit
   * Ketch's `legalBasisDefault`.
   *
   * Reverse: mirrors Ketch consent changes back to the gdpr cookie
   * (information / adverts booleans + IAB tcString) so a rollback to the
   * legacy CMP doesn't re-prompt users.
   * Default: false
   */
  enableLegacyConsentSync?: boolean;
  /**
   * Enable resolveConsentId() call before Ketch script injection.
   * When true, POSTs to the consent-id endpoint to ensure __Secure-consent_id
   * is set before Ketch boots. Call is skipped if the cookie is already present.
   * On failure (retry then fail-closed), Ketch is not loaded for the session.
   * Default: false
   */
  enableConsentIdService?: boolean;
  /** Custom URL for the consent-id endpoint. Default: /g/purpose-scoped-id-service/identifiers/consent-id */
  consentIdServiceUrl?: string;
}

/**
 * Configuration for loadCMPSDK
 */
export interface CMPSDKConfig {
  /** Override script URL (takes precedence over initTrackingPreferences URL) */
  scriptUrl?: string;
  /** Callback when SDK loads successfully */
  onLoad?: () => void;
  /** Callback when SDK fails to load */
  onError?: () => void;
  /**
   * Callback when Ketch decides to show the consent banner.
   * Triggered by Ketch's 'willShowExperience' event when the experience type is 'consent'.
   * Use this to show your custom consent banner UI.
   * Note: This does NOT fire for Preference Center - only for the initial consent banner.
   */
  onShowConsentExperience?: () => void;
  /**
   * Callback when Ketch hides the consent banner.
   * Triggered by Ketch's 'hideExperience' event when the consent banner is visible.
   * Use this to hide your custom consent banner UI.
   * Note: This only fires when the consent banner was previously shown via willShowExperience.
   */
  onHideConsentExperience?: () => void;
  /**
   * Timeout in milliseconds for SDK to load.
   * If exceeded, triggers onError and applies fallback (if enabled).
   * Default: 10000ms (10 seconds)
   */
  timeoutMs?: number;
  /**
   * Automatically apply reject-all fallback consent when SDK fails to load.
   * Only applies when no previous consent exists.
   * Fallback is session-only (in-memory) and not persisted to cookie.
   * Default: false
   */
  enableFallback?: boolean;
}

/**
 * Ketch _swb_consent_ cookie structure
 * Encoding: URL-encoded Base64 JSON
 */
export interface KetchConsentCookie {
  /** Unix timestamp when consent was collected */
  collectedAt: number;
  /** Ketch environment code (e.g., "production") */
  environmentCode: string;
  /** User identities map */
  identities: Record<string, string>;
  /** Jurisdiction code (e.g., "gdpr") */
  jurisdictionCode: string;
  /** Ketch property code */
  propertyCode: string;
  /** Consent purposes with allowed status */
  purposes: {
    [purposeCode: string]: {
      /** Whether this purpose is allowed - NOTE: string "true"/"false", not boolean */
      allowed: 'true' | 'false';
      /** Legal basis for this purpose */
      legalBasisCode: string;
    };
  };
  /** Context metadata about how consent was collected */
  context?: {
    /** Source of the consent record (e.g., "legalBasisDefault", "router.setConsent") */
    source?: string;
  };
  /**
   * Whether the user has actively interacted with the consent experience.
   * `false` means the cookie was auto-set by the SDK without user interaction.
   * `true` means the user made an active consent decision.
   * Absent in older cookie formats.
   */
  interactive?: boolean;
  // NOTE: TC string (tcf) and GPP string are NOT stored in the cookie.
  // They are only available via Ketch SDK: ketch('getConsent', (consent) => consent.protocols?.tcf)
}

// =============================================================================
// Phase 2: Consent Action Types
// =============================================================================

/**
 * Vendor consent status
 */
export interface VendorConsents {
  /** TCF vendor consents */
  tcf?: Record<string, 'granted' | 'denied'>;
  /** Google vendor consents */
  google?: Record<string, 'granted' | 'denied'>;
}

/**
 * Vendor purpose entry from Ketch full config
 */
export interface KetchVendorPurpose {
  /** TCF purpose ID */
  id: number;
  /** Legal basis (e.g., "Consent", "Legitimate interest") */
  legalBasis?: string;
  /** Purpose name */
  name?: string;
  /** Data retention period in days for this specific purpose */
  retention?: number;
}

/**
 * Data category entry from TCF vendor metadata (systems.tcf[].tcfMetadata.dataDeclaration)
 */
export interface KetchDataDeclaration {
  /** Category name (e.g., "IP addresses", "Device identifiers") */
  name: string;
  /** Category description */
  description?: string;
}

/**
 * TCF feature/special feature entry with numeric ID (from systems.tcf[].tcfMetadata)
 */
export interface KetchTCFFeature {
  /** TCF feature ID */
  id: number;
  /** Feature name */
  name: string;
  /** Legal basis (e.g., "Disclosure", "Consent - Opt In") */
  legalBasis?: string;
  /** Feature description (enriched from GVL) */
  description?: string;
  /** Illustration examples (enriched from GVL) */
  illustrations?: string[];
}

/**
 * TCF special purpose entry with numeric ID (from systems.tcf[].tcfMetadata)
 */
export interface KetchTCFSpecialPurpose {
  /** TCF special purpose ID */
  id: number;
  /** Special purpose name */
  name: string;
  /** Legal basis (e.g., "Legitimate Interest - Non-Objectable") */
  legalBasis?: string;
  /** Special purpose description (enriched from GVL) */
  description?: string;
  /** Illustration examples (enriched from GVL) */
  illustrations?: string[];
}

/**
 * Vendor entry from Ketch full config
 */
export interface KetchVendor {
  /** Vendor ID (TCF vendor ID as string) */
  id: string;
  /** Vendor name */
  name?: string;
  /** Vendor privacy policy URL */
  policyUrl?: string;
  /** Purposes requiring consent */
  purposes?: KetchVendorPurpose[];
  /** Purposes under legitimate interest */
  legIntPurposes?: Array<{ id: number }>;
  /** Special purposes (with numeric IDs from TCF metadata) */
  specialPurposes?: KetchTCFSpecialPurpose[];
  /** Features (with numeric IDs from TCF metadata) */
  features?: KetchTCFFeature[];
  /** Special features (with numeric IDs from TCF metadata) */
  specialFeatures?: KetchTCFFeature[];
  /** Maximum cookie age in seconds */
  cookieMaxAgeSeconds?: number | null;
  /** Whether the vendor uses cookies */
  usesCookies?: boolean;
  /** Whether the vendor uses non-cookie access */
  usesNonCookieAccess?: boolean;
  /** Whether the vendor refreshes cookies on each visit */
  cookieRefresh?: boolean;
  /** Purposes that accept either consent or legitimate interest (from TCF metadata) */
  flexiblePurposes?: Array<{ id: number }>;
  /** Legitimate interest claim URL (from TCF metadata) */
  legIntClaim?: string;
  /** Device storage disclosure URL (from TCF metadata) */
  deviceStorageDisclosureUrl?: string;
  /** Data categories declared by this vendor (from TCF metadata) */
  dataDeclaration?: KetchDataDeclaration[];
  /** Data retention period in days (from TCF metadata) */
  dataRetention?: number;
}

/**
 * Consent action for updateConsent()
 *
 * @example
 * // Accept all
 * { action: 'accept_all' }
 *
 * @example
 * // Reject all
 * { action: 'reject_all' }
 *
 * @example
 * // Set specific purposes
 * { action: 'set', purposes: { improved_experience: true, advertising_and_related_technologies: false } }
 */
export type ConsentAction =
  | { action: 'accept_all' }
  | { action: 'reject_all' }
  | {
      action: 'set';
      purposes?: KetchPurposes;
      vendorConsents?: VendorConsents;
    };

/**
 * Purpose configuration for Privacy Centre UI
 */
export interface Purpose {
  /** Purpose code (e.g., "analytics", "marketing") */
  code: string;
  /** Human-readable name */
  name: string;
  /** Optional description */
  description?: string;
  /** Legal basis code (e.g., "consent_optin", "legitimateinterest") */
  legalBasisCode: string;
  /** Illustration strings from Ketch config (used in Privacy Centre UI) */
  illustrations?: string[];
  /** Systems/frameworks associated with this purpose (e.g., TCF vendor IDs) */
  systems?: { tcf?: string[] | null };
}

/**
 * Consent configuration returned by getConsentConfig()
 * Used to build Privacy Centre UI
 */
export interface ConsentConfig {
  /** Available consent purposes */
  purposes: Purpose[];
  /** Current jurisdiction (e.g., "gdpr") */
  jurisdiction: string;
  /** Vendors enriched with TCF metadata (dataDeclaration, dataRetention, numeric IDs) */
  vendors?: KetchVendor[];
  /** Service configuration (e.g., GVL host for TCF enrichment) */
  services?: { gvlHost?: string };
  /** TCF purposes (tcf_purpose_1 through tcf_purpose_11) with descriptions, illustrations, and vendor IDs */
  tcfPurposes?: Purpose[];
  /** Deduplicated TCF features across all vendors */
  features?: KetchTCFFeature[];
  /** Deduplicated TCF special features across all vendors */
  specialFeatures?: KetchTCFFeature[];
  /** Deduplicated TCF special purposes across all vendors */
  specialPurposes?: KetchTCFSpecialPurpose[];
}

// =============================================================================
// Raw Ketch API response types (internal — used by ketch-consent-api.ts)
// =============================================================================

/**
 * Shape of a single purpose in the raw Ketch full config response.
 * Used to type the result of getCachedFullConfig() for dot-notation access.
 */
export interface RawKetchPurpose {
  code?: string;
  name?: string;
  description?: string;
  legalBasisCode?: string;
  illustrations?: string[];
  systems?: { tcf?: string[] | null };
}

/**
 * TCF metadata per vendor from systems.tcf[].tcfMetadata in Ketch full config.
 * Contains enriched data not available on the top-level vendors array.
 */
export interface RawTCFVendorMetadata {
  purposes?: Array<{
    id: number;
    legalBasis?: string;
    name?: string;
    retention?: number;
  }> | null;
  legIntPurposes?: Array<{ id: number }> | null;
  flexiblePurposes?: Array<{ id: number }> | null;
  specialPurposes?: Array<{
    id: number;
    name: string;
    legalBasis?: string;
  }> | null;
  features?: Array<{ id: number; name: string; legalBasis?: string }> | null;
  specialFeatures?: Array<{
    id: number;
    name: string;
    legalBasis?: string;
  }> | null;
  dataRetention?: number;
  dataDeclaration?: Array<{ name: string; description?: string }> | null;
  cookieMaxAgeSeconds?: number;
  cookieRefresh?: boolean;
  usesCookies?: boolean;
  usesNonCookieAccess?: boolean;
  legIntClaim?: string;
  deviceStorageDisclosure?: string;
}

/**
 * TCF vendor entry from systems.tcf[] in Ketch full config.
 */
export interface RawTCFSystemVendor {
  id: string;
  name?: string;
  privacyPolicy?: string;
  tcfMetadata?: RawTCFVendorMetadata;
}

/**
 * Raw vendor shape from Ketch getFullConfig — before TCF metadata enrichment.
 * Features/specialFeatures may lack numeric IDs at this stage.
 */
export interface RawKetchVendor {
  id: string;
  name?: string;
  policyUrl?: string;
  purposes?: Array<{ id: number; legalBasis?: string; name?: string }>;
  legIntPurposes?: Array<{ id: number }>;
  specialPurposes?: Array<{ id?: number; name?: string }>;
  features?: Array<{ name?: string }>;
  specialFeatures?: Array<{ name?: string }>;
  cookieMaxAgeSeconds?: number | null;
  usesCookies?: boolean;
  usesNonCookieAccess?: boolean;
}

/**
 * Top-level shape of the raw Ketch getFullConfig() response.
 */
export interface RawKetchFullConfig {
  purposes?: RawKetchPurpose[];
  vendors?: RawKetchVendor[];
  jurisdiction?: string;
  services?: { gvlHost?: string };
  systems?: { tcf?: RawTCFSystemVendor[] };
}

/**
 * Interface for Ketch TCF localStorage data.
 * The value is a base64 encoded JSON string containing tcString.
 */
export interface KetchTCFData {
  tcString?: string;
  [key: string]: unknown;
}

// =============================================================================
// Consent Action Constants
// =============================================================================

/**
 * Constants for consent actions to prevent typos when calling updateConsent()
 *
 * @example
 * import {
 *   CONSENT_ACTIONS,
 *   KETCH_PURPOSE_CODES,
 *   updateConsent,
 * } from 'saddlebag-tracking-preferences';
 *
 * updateConsent({ action: CONSENT_ACTIONS.ACCEPT_ALL });
 * updateConsent({ action: CONSENT_ACTIONS.REJECT_ALL });
 * updateConsent({
 *   action: CONSENT_ACTIONS.SET,
 *   purposes: { [KETCH_PURPOSE_CODES.INFORMATION]: true },
 * });
 */
export const CONSENT_ACTIONS = {
  /** Accept all consent purposes */
  ACCEPT_ALL: 'accept_all',
  /** Reject all consent purposes */
  REJECT_ALL: 'reject_all',
  /** Set specific consent purposes */
  SET: 'set',
} as const;

/**
 * Ketch purpose codes used by Skyscanner's Ketch property configuration.
 *
 * These are the canonical codes the Ketch SDK reads/writes; `ketch-mapping`
 * bridges them to saddlebag's internal `information` / `adverts` preferences.
 * Update here if the Ketch property's purpose codes change.
 */
export const KETCH_PURPOSE_CODES = {
  /** Maps to PREFERENCES.INFORMATION (tracking / analytics consent) */
  INFORMATION: 'improved_experience',
  /** Maps to PREFERENCES.ADVERTS (targeted advertising consent) */
  ADVERTS: 'advertising_and_related_technologies',
  /** Always-true legitimate-interest acknowledgement; Ketch requires it on every setConsent call. */
  ESSENTIAL_SERVICES: 'essential_services',
} as const;

export type KetchPurposeCode =
  (typeof KETCH_PURPOSE_CODES)[keyof typeof KETCH_PURPOSE_CODES];

/**
 * Ketch consent source values written to the `context.source` field of
 * the _swb_consent_ cookie. Used to distinguish auto-set defaults from
 * actual user consent decisions.
 */
export const KETCH_CONSENT_SOURCES = {
  /** Ketch auto-writes this on SDK init — user has NOT interacted with the banner */
  LEGAL_BASIS_DEFAULT: 'legalBasisDefault',
} as const;

// =============================================================================
// Normalized Consent Data (for getConsent)
// =============================================================================

/** Purpose consent data with allowed status and legal basis */
export interface PurposeConsent {
  allowed: boolean;
  legalBasisCode: string;
}

/**
 * Ketch purposes boolean map for consent updates.
 * Used by updateConsent() and mapPreferencesToKetchPurposes().
 */
export interface KetchPurposes {
  /**
   * Purpose codes keyed by the strings in `KETCH_PURPOSE_CODES` or any other
   * code configured on the Ketch property. Use the constants to avoid typos,
   * e.g. `{ [KETCH_PURPOSE_CODES.INFORMATION]: true }`.
   */
  [key: string]: boolean | undefined;
}

/**
 * Normalized Ketch consent data returned by getConsent()
 * Converts raw cookie format (string "true"/"false") to booleans
 */
export interface KetchConsentData {
  /**
   * Consent purposes keyed by their Ketch purpose code with normalized boolean
   * `allowed` values. Use `KETCH_PURPOSE_CODES` to read well-known codes, e.g.
   * `consent.purposes[KETCH_PURPOSE_CODES.INFORMATION]`.
   */
  purposes: {
    [key: string]: PurposeConsent | undefined;
  };
  /** Jurisdiction code (e.g., "gdpr") */
  jurisdiction: string;
  /** Unix timestamp when consent was collected */
  collectedAt?: number;
  /** Ketch property code */
  propertyCode?: string;
}
